/**
 * Rock UCS (No Strain) — field metadata, options, and defaults (data only, no JSX).
 *
 * BUG-2026-0138: the sheet now mirrors TCS / UCS-with-strain —
 *  - exactly ONE specimen (item 4; the count options are gone, the UI pins
 *    specimenCount to 1)
 *  - core size / condition vocabularies come from rock-ucs/rockUcsFields.ts
 *    (shared with Test A — the conformity dial positions are defined for
 *    HQ/NQ only)
 *  - conformity gets sideStraightnessMm + dial-reading rows; ultrasonic gets
 *    4 velocity/time rows (both reuse the rock-ucs empty-row factories)
 *
 * Failure vocabulary is TCS-style (FailureMode / PlaneType / angle).
 * Options trace to backend/src/constants/rockUcsNs.ts + §1.3 golden fixture.
 */
import type { RockUCSNSGeneral, RockUCSNSSpecimen } from '../../../types/rockUcsNs';
import { ULTRASONIC_TOLERANCE_DEFAULT } from '../rock-ucs/rockUcsFields';

/**
 * Failure mode options — TCS-style vocabulary (not BTS FailureType).
 * Matches backend constants/rockUcsNs.ts UCSNS_FAILURE_MODES.
 * Golden: SN25-437_5.92 = "Axial Cleavage".
 */
export const UCSNS_FAILURE_MODE_OPTIONS = [
  'Shear',
  'Multiple Shear',
  'Axial Cleavage',
  'Shear & Axial Cleavage',
  'Multiple Shear & Axial Cleavage',
  'Fragmented',
] as const;

/**
 * Plane type options (primary and secondary failure plane).
 * Matches backend constants/rockUcsNs.ts UCSNS_PLANE_TYPES.
 * Golden: SN25-437_5.92 primary = "rock matrix".
 */
export const UCSNS_PLANE_TYPE_OPTIONS = [
  'vein',
  'microdefect',
  'rock matrix',
  'bedding',
  'foliation',
  'intrusion',
] as const;

// ── Empty-state defaults ────────────────────────────────────────────────────────

/**
 * Default General row — specimenCount pinned to 1 (BUG-2026-0138 item 4);
 * coreSize HQ + ultrasonic tolerance 0.35 mm, matching Test A's defaults.
 */
export const EMPTY_ROCK_UCSNS_GENERAL: RockUCSNSGeneral = {
  specimenCount: 1,
  coreSize: 'HQ',
  specimenCondition: 'as received',
  preparedBy: null,
  machineKey: null,
  testDate: null,
  testedBy: null,
  sideStraightnessMm: null,
  conformityFailReason: null,
  ultrasonicCheckTolerance: ULTRASONIC_TOLERANCE_DEFAULT,
  remarksText: null,
  additionalObservations: null,
};

/**
 * Factory for a blank specimen row by specimenNumber.
 *
 * Key Test B inputs:
 *   rateOfLoadingKNs — K38 in the asset workbook (1.2 kN/s golden)
 *   maxLoadKN       — T37 in the asset workbook (155.2 kN golden)
 *   durationS       — manual; K41 in the asset workbook (146.1 s golden)
 *
 * Does NOT include gauge length / chain length / confining stress
 * (item 1.5 DROP) or any BTS-style fields.
 */
export const emptyUcsNsSpecimen = (specimenNumber: number): RockUCSNSSpecimen => ({
  specimenNumber,

  // Geometry — 6 diameters + 4 lengths (TCS-style; workbook I29:S29, I30:P30)
  diameter1: null,
  diameter2: null,
  diameter3: null,
  diameter4: null,
  diameter5: null,
  diameter6: null,
  length1: null,
  length2: null,
  length3: null,
  length4: null,
  massG: null,

  // Leeb hardness (workbook B39 ends, D39 edge)
  leebHardnessEnds: null,
  leebHardnessEdge: null,

  // Rate of loading (workbook K38 = 1.2 kN/s golden)
  rateOfLoadingKNs: null,

  // Test result (workbook T37 = 155.2 kN golden → 87.062 MPa derived)
  maxLoadKN: null,

  // Duration (workbook K41 = 146.1 s golden; manual entry, not derived)
  durationS: null,

  // TCS-style failure vocabulary (not BTS FailureType/FractureMode)
  failureMode: null,
  primaryPlaneType: null,
  primaryPlaneAngleDeg: null,
  secondaryPlaneType: null,
  secondaryPlaneAngleDeg: null,
  failureDescriptionOverride: null,
  failureRemarks: null,
});
